/**
 * Grid Snap — computes discrete grid positions for the Layout Nudge system.
 *
 * Two modes:
 * 1. Grid snap (for logo nudge / small ranges ≤80px): Discrete positions evenly
 *    spaced within constraint range.
 * 2. Edge-aware guide snap (for content nudge / large ranges >80px): Free-form
 *    drag with snapping to slide guide lines when block edges come close.
 *
 * Mode selection is automatic based on constraint range extent.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// EDGE-AWARE GUIDE SNAP (Session 49)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Horizontal guide lines — meaningful Y positions on the 1080×1350 canvas.
 * These correspond to the visual guides rendered in SlideCanvas (.slide-guide-*).
 */
export const GUIDE_LINES_Y = [
  { y: 60,   id: 'margin-top',  label: 'margin' },
  { y: 449,  id: 'third-h1',    label: '⅓' },
  { y: 674,  id: 'center-h',    label: 'center' },
  { y: 899,  id: 'third-h2',    label: '⅔' },
  { y: 1290, id: 'margin-bottom', label: 'margin' },
];

/**
 * Vertical guide lines — meaningful X positions on the 1080×1350 canvas.
 * These correspond to the visual guides rendered in SlideCanvas (.slide-guide-*).
 */
export const GUIDE_LINES_X = [
  { x: 60,   id: 'margin-left',  label: 'margin' },
  { x: 359,  id: 'third-v1',     label: '⅓' },
  { x: 539,  id: 'center-v',     label: 'center' },
  { x: 719,  id: 'third-v2',     label: '⅔' },
  { x: 1020, id: 'margin-right', label: 'margin' },
];

/** Snap threshold in canvas pixels (~7px on screen at 0.35 scale) */
export const SNAP_THRESHOLD = 20;

/** Home position (dy=0) snap threshold */
export const HOME_THRESHOLD = 15;

/**
 * Determine if a constraint should use edge-aware snapping (vs grid snap).
 * Large ranges (>80px extent) benefit from free-form + guides.
 * Small ranges (logos, ±40px) benefit from precise grid snap.
 */
export function shouldUseEdgeSnap(constraint) {
  if (!constraint) return false;
  const axis = constraint.axis || 'y';
  let maxExtent = 0;

  if (axis === 'y' || axis === 'both') {
    const [minY, maxY] = constraint.range?.y || [0, 0];
    maxExtent = Math.max(maxExtent, Math.abs(minY), Math.abs(maxY));
  }
  if (axis === 'x' || axis === 'both') {
    const [minX, maxX] = constraint.range?.x || [0, 0];
    maxExtent = Math.max(maxExtent, Math.abs(minX), Math.abs(maxX));
  }

  return maxExtent > 80;
}

/**
 * Compute edge-aware snap for a given raw offset.
 *
 * Checks if the block's top, center, or bottom edge aligns with any guide line.
 * Returns the snapped dy and metadata about which guide/edge matched.
 *
 * @param {number} rawDy - Raw (unclamped) dy offset from drag
 * @param {number} baseTop - Block's top Y in canvas space when dy=0
 * @param {number} blockHeight - Block's height in canvas space
 * @param {number[]} rangeY - [minDy, maxDy] constraint range
 * @returns {{ dy: number, snapped: boolean, guideId: string|null, allGuideIds: string[], edge: string|null, label: string|null }}
 */
export function computeEdgeSnap(rawDy, baseTop, blockHeight, rangeY) {
  const [minDy, maxDy] = rangeY;

  // Clamp raw value to constraint range
  const clampedDy = Math.max(minDy, Math.min(maxDy, rawDy));

  // Check home position (dy=0) first — always a snap target
  if (Math.abs(clampedDy) < HOME_THRESHOLD) {
    return { dy: 0, snapped: true, guideId: 'home', allGuideIds: [], edge: 'home', label: 'home' };
  }

  // Check each guide line × each edge (top, center, bottom)
  let bestSnap = null;
  let bestDist = SNAP_THRESHOLD; // Only snap if within threshold
  const allMatching = [];        // All guides with ANY edge within threshold

  for (const guide of GUIDE_LINES_Y) {
    const dyForTop = guide.y - baseTop;
    const dyForCenter = guide.y - baseTop - blockHeight / 2;
    const dyForBottom = guide.y - baseTop - blockHeight;

    const candidates = [
      { dy: dyForTop, edge: 'top' },
      { dy: dyForCenter, edge: 'center' },
      { dy: dyForBottom, edge: 'bottom' },
    ];

    let guideMatches = false;
    for (const candidate of candidates) {
      if (candidate.dy < minDy || candidate.dy > maxDy) continue;

      const dist = Math.abs(clampedDy - candidate.dy);
      if (dist < SNAP_THRESHOLD) {
        guideMatches = true;
        if (dist < bestDist) {
          bestDist = dist;
          bestSnap = {
            dy: candidate.dy,
            snapped: true,
            guideId: guide.id,
            edge: candidate.edge,
            label: guide.label,
          };
        }
      }
    }
    if (guideMatches) allMatching.push(guide.id);
  }

  if (bestSnap) {
    bestSnap.allGuideIds = allMatching;
    return bestSnap;
  }

  return { dy: clampedDy, snapped: false, guideId: null, allGuideIds: [], edge: null, label: null };
}

/**
 * Compute edge-aware snap for the X axis.
 *
 * Checks if the block's left, center, or right edge aligns with any vertical guide line.
 *
 * @param {number} rawDx - Raw (unclamped) dx offset from drag
 * @param {number} baseLeft - Block's left X in canvas space when dx=0
 * @param {number} blockWidth - Block's width in canvas space
 * @param {number[]} rangeX - [minDx, maxDx] constraint range
 * @returns {{ dx: number, snapped: boolean, guideId: string|null, allGuideIds: string[], edge: string|null, label: string|null }}
 */
export function computeEdgeSnapX(rawDx, baseLeft, blockWidth, rangeX) {
  const [minDx, maxDx] = rangeX;

  const clampedDx = Math.max(minDx, Math.min(maxDx, rawDx));

  if (Math.abs(clampedDx) < HOME_THRESHOLD) {
    return { dx: 0, snapped: true, guideId: 'home', allGuideIds: [], edge: 'home', label: 'home' };
  }

  const useLeftEdgeOnly = blockWidth > 540;

  let bestSnap = null;
  let bestDist = SNAP_THRESHOLD;
  const allMatching = [];

  for (const guide of GUIDE_LINES_X) {
    const dxForLeft = guide.x - baseLeft;
    const dxForCenter = guide.x - baseLeft - blockWidth / 2;
    const dxForRight = guide.x - baseLeft - blockWidth;

    const candidates = useLeftEdgeOnly
      ? [{ dx: dxForLeft, edge: 'left' }]
      : [
          { dx: dxForLeft, edge: 'left' },
          { dx: dxForCenter, edge: 'center' },
          { dx: dxForRight, edge: 'right' },
        ];

    let guideMatches = false;
    for (const candidate of candidates) {
      if (candidate.dx < minDx || candidate.dx > maxDx) continue;

      const dist = Math.abs(clampedDx - candidate.dx);
      if (dist < SNAP_THRESHOLD) {
        guideMatches = true;
        if (dist < bestDist) {
          bestDist = dist;
          bestSnap = {
            dx: candidate.dx,
            snapped: true,
            guideId: guide.id,
            edge: candidate.edge,
            label: guide.label,
          };
        }
      }
    }
    if (guideMatches) allMatching.push(guide.id);
  }

  if (bestSnap) {
    bestSnap.allGuideIds = allMatching;
    return bestSnap;
  }

  return { dx: clampedDx, snapped: false, guideId: null, allGuideIds: [], edge: null, label: null };
}


// ═══════════════════════════════════════════════════════════════════════════════
// GRID SNAP (original — used for logo nudge / small ranges)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Compute the grid step for a given axis range [min, max].
 * Returns positions that ALWAYS include 0 and the range boundaries.
 *
 * @param {number} min - Lower bound (e.g., -300)
 * @param {number} max - Upper bound (e.g., 300)
 * @returns {number[]} Sorted array of valid snap positions
 */
export function computeGridPositions(min, max) {
  if (min === 0 && max === 0) return [0];

  const absMin = Math.abs(min);
  const absMax = Math.abs(max);
  const maxExtent = Math.max(absMin, absMax);

  if (maxExtent === 0) return [0];

  // Find best step: a divisor of maxExtent giving 5–11 total positions closest to 9
  let bestStep = maxExtent; // fallback: 2 positions (0 + boundary)
  let bestScore = Infinity;
  let bestTotal = 0;

  for (let n = 2; n <= 12; n++) {
    if (maxExtent % n === 0) {
      const step = maxExtent / n;
      const negCount = absMin > 0 ? Math.floor(absMin / step) : 0;
      const posCount = absMax > 0 ? Math.floor(absMax / step) : 0;
      const total = negCount + 1 + posCount;
      if (total >= 5 && total <= 11) {
        const score = Math.abs(total - 9);
        // Prefer closer to 9; on tie prefer MORE positions (finer control)
        if (score < bestScore || (score === bestScore && total > bestTotal)) {
          bestScore = score;
          bestStep = step;
          bestTotal = total;
        }
      }
    }
  }

  // If no clean divisor found, try approximate: round maxExtent/4 to nearest 8
  if (bestScore === Infinity) {
    const approx = Math.max(8, Math.round(maxExtent / 4 / 8) * 8);
    bestStep = approx;
  }

  // Generate positions from 0 outward
  const positions = new Set([0]);

  // Negative direction
  for (let i = 1; i * bestStep <= absMin; i++) {
    positions.add(-i * bestStep);
  }
  // Positive direction
  for (let i = 1; i * bestStep <= absMax; i++) {
    positions.add(i * bestStep);
  }

  // Always include range boundaries (ensures full range is reachable)
  if (min !== 0) positions.add(min);
  if (max !== 0) positions.add(max);

  return [...positions].sort((a, b) => a - b);
}

/**
 * Compute grid for both axes of a constraint.
 *
 * @param {{ axis: string, range: { x?: [number, number], y?: [number, number] } }} constraint
 * @returns {{ x: number[], y: number[] }}
 */
export function computeConstraintGrid(constraint) {
  if (!constraint) return { x: [0], y: [0] };

  const axis = constraint.axis || 'y';
  let xPositions = [0];
  let yPositions = [0];

  if (axis === 'y' || axis === 'both') {
    const [minY, maxY] = constraint.range?.y || [0, 0];
    yPositions = computeGridPositions(minY, maxY);
  }

  if (axis === 'x' || axis === 'both') {
    const [minX, maxX] = constraint.range?.x || [0, 0];
    xPositions = computeGridPositions(minX, maxX);
  }

  return { x: xPositions, y: yPositions };
}

/**
 * Snap a value to the nearest grid position.
 *
 * @param {number} value - Raw value to snap
 * @param {number[]} positions - Sorted array of valid positions
 * @returns {number} Nearest grid position
 */
export function snapToGrid(value, positions) {
  if (!positions || positions.length === 0) return 0;
  if (positions.length === 1) return positions[0];

  let closest = positions[0];
  let minDist = Math.abs(value - closest);

  for (let i = 1; i < positions.length; i++) {
    const dist = Math.abs(value - positions[i]);
    if (dist < minDist) {
      minDist = dist;
      closest = positions[i];
    }
  }

  return closest;
}

/**
 * Migrate old layoutNudge format (group keys at top level)
 * to new template-scoped format (template ID → group keys).
 *
 * Old: { content: { dx, dy }, logo: { dx, dy } }
 * New: { 'forest-classic': { content: { dx, dy }, logo: { dx, dy } } }
 *
 * @param {object|null} layoutNudge - Existing layoutNudge data
 * @param {string} templateId - Current template ID
 * @returns {object|null} Template-scoped layoutNudge or null
 */
export function migrateLayoutNudge(layoutNudge, templateId) {
  if (!layoutNudge) return null;

  // Detect old format: top-level keys are group IDs (content/logo) with dx/dy
  const isOldFormat = (layoutNudge.content && ('dx' in layoutNudge.content || 'dy' in layoutNudge.content))
    || (layoutNudge.logo && ('dx' in layoutNudge.logo || 'dy' in layoutNudge.logo));

  if (isOldFormat) {
    // Migrate to template-scoped
    return { [templateId]: layoutNudge };
  }

  // Already new format (keys are template IDs)
  return layoutNudge;
}
